import { BillingSubscriptionItem, Product } from '../../../api/types'
import { logger } from '../../../utils/logging'

export const getPlanUnitAmount = (product: Product): number | undefined => {
  return product.defaultPrice.unitAmount
}

/* We use the following pattern for graduated pricing on metered products:
 *  If price.tiers is undefined, then price.unitAmount is the price.
 *  Otherwise,are exactly 2 tiers. The first tier is free, and the second tier is paid.
 * [
 *    { upTo: 10, unitAmount: 0 },
 *    { upTo: Infinity, unitAmount: 250 },
 *]
 */
const freeTierIndex = 0
const paidTierIndex = 1

export const getPaidTierUnitAmount = (product: Product): number | undefined => {
  try {
    const tiers = product?.defaultPrice?.tiers
    if (!tiers) return product.defaultPrice.unitAmount
    const paidTier = tiers[paidTierIndex]
    if (!paidTier) return undefined
    return paidTier.unitAmount
  } catch (error) {
    logger.error('Error getting paid tier unit amount', { error, product })
    throw error
  }
}

export const getFreeTierUpTo = (product: Product): number | undefined => {
  try {
    const { tiers } = product.defaultPrice
    if (!tiers) return 0
    const freeTier = tiers[freeTierIndex]
    if (!freeTier) return undefined
    return freeTier.upTo
  } catch (error) {
    logger.error('Error getting free tier up to', { error, product })
    throw error
  }
}

const liftMaybe =
  <T, S>(f: (input: T) => S) =>
  (input: T | undefined): S | undefined => {
    if (input === undefined) {
      return undefined
    }
    return f(input)
  }

const getMaybePlanUnitAmount = liftMaybe(getPlanUnitAmount)
const getMaybePaidTierUnitAmount = liftMaybe(getPaidTierUnitAmount)
const getMaybeFreeTierUpTo = liftMaybe(getFreeTierUpTo)

const centsToDollars = (cents: number) => cents / 100
const maybeCentsToDollars = (cents: number | undefined) => (cents !== undefined ? centsToDollars(cents) : undefined)
export const productIdIsPro = (productId: string | undefined, allProducts: Product[]): boolean => {
  const product = allProducts.find((p) => p.id === productId)
  if (!product) return false
  return product.metadata.planType === 'professional'
}

export function prepayProductIsAvailable(
  product: Product,
  selectedPlanHasPayG: boolean,
  selectedPlanIsPro: boolean
): boolean {
  const productIsProOnly = product.metadata.proOnly === 'true'
  if (!selectedPlanHasPayG) return false
  return !productIsProOnly || selectedPlanIsPro
}

export function prepayProductIsAvailableSelfServe(
  product: Product,
  selectedPlanHasPayG: boolean,
  selectedPlanIsPro: boolean
): boolean {
  return (
    prepayProductIsAvailable(product, selectedPlanHasPayG, selectedPlanIsPro) &&
    product.metadata.canSelfServe === 'true'
  )
}

export const getCurrentPlan = (subscriptionItems: BillingSubscriptionItem[]) =>
  subscriptionItems.find((item) => item.product.metadata.productType === 'plan')?.product

export const getCurrentCommitmentProduct = (subscriptionItems: BillingSubscriptionItem[], usageType: 'file' | 'PDV') =>
  subscriptionItems.find(
    (item: any) => item?.product?.metadata?.productType === 'usage' && item?.product?.metadata?.usageType === usageType
  )?.product

export const getPlanChange = ({
  subscriptionItems,
  starterProduct,
  productsData,
  selectedPlanId,
  selectedFileUsageId,
  selectedPDVUsageId,
  freeFileAllowance,
  freePDVAllowance,
}: {
  subscriptionItems: BillingSubscriptionItem[]
  starterProduct: Product | undefined
  productsData: Product[]
  selectedPlanId: string | undefined
  selectedFileUsageId: string | undefined
  selectedPDVUsageId: string | undefined
  freeFileAllowance: number
  freePDVAllowance: number
}) => {
  const currentPlan = getCurrentPlan(subscriptionItems) || starterProduct

  const currentPlanPrice = maybeCentsToDollars(getMaybePlanUnitAmount(currentPlan))!

  const newPlan = productsData.find((product) => product.id === selectedPlanId) || starterProduct

  const newPlanPrice = maybeCentsToDollars(getMaybePlanUnitAmount(newPlan))

  const currentFileCommitment = getCurrentCommitmentProduct(subscriptionItems, 'file')

  const newFileCommitment = productsData.find((product) => product.id === selectedFileUsageId)

  const currentPDVCommitment = getCurrentCommitmentProduct(subscriptionItems, 'PDV')

  const newPDVCommitment = productsData.find((product) => product.id === selectedPDVUsageId)

  const currentFileCommitmentPrice = maybeCentsToDollars(getMaybePaidTierUnitAmount(currentFileCommitment))!

  const newFileCommitmentPrice = maybeCentsToDollars(getMaybePaidTierUnitAmount(newFileCommitment))!

  const currentPDVCommitmentPrice = maybeCentsToDollars(getMaybePaidTierUnitAmount(currentPDVCommitment))!

  const newPDVCommitmentPrice = maybeCentsToDollars(getMaybePaidTierUnitAmount(newPDVCommitment))!

  const newPlanText = newPlan?.name
  const currentPlanText = currentPlan?.name
  const currentPlanId = currentPlan?.id
  const currentFileCommitmentText = currentFileCommitment?.metadata?.commitment
  const newFileCommitmentText = newFileCommitment?.metadata?.commitment
  const currentPDVCommitmentText = currentPDVCommitment?.metadata?.commitment
  const newPDVCommitmentText = newPDVCommitment?.metadata?.commitment

  const newFileCommitmentPrepay = productsData.find((product) => product.id === newFileCommitment?.metadata?.prepayId)

  const newFileCommitmentPrepayPrice = maybeCentsToDollars(getMaybePlanUnitAmount(newFileCommitmentPrepay)) || 0

  const newFileCommitmentUpTo = freeFileAllowance + getMaybeFreeTierUpTo(newFileCommitment)!

  const newPDVCommitmentPrepay = productsData.find((product) => product.id === newPDVCommitment?.metadata?.prepayId)

  const newPDVCommitmentPrepayPrice = maybeCentsToDollars(getMaybePlanUnitAmount(newPDVCommitmentPrepay)) || 0

  const newPDVCommitmentUpTo = freePDVAllowance + getMaybeFreeTierUpTo(newPDVCommitment)!

  return {
    currentPlanText,
    currentPlanPrice,
    currentPlanId,
    currentFileCommitmentText,
    currentFileCommitmentPrice,
    currentPDVCommitmentText,
    currentPDVCommitmentPrice,
    newPlanText,
    newPlanPrice,
    newFileCommitmentText,
    newFileCommitmentPrice,
    newPDVCommitmentText,
    newPDVCommitmentPrice,
    newFileCommitmentPrepayPrice,
    newFileCommitmentUpTo,
    newPDVCommitmentPrepayPrice,
    newPDVCommitmentUpTo,
  }
}

export const calculatePdvDollarAmount = (pdvUsage: number, pdvMeteredProduct: Product) => {
  const prepaidVolume = getFreeTierUpTo(pdvMeteredProduct)!
  const unitAmount = getPaidTierUnitAmount(pdvMeteredProduct)!
  return Math.ceil(Math.max(0, pdvUsage - prepaidVolume * 1_000_000) * (1 / 1_000_000)) * unitAmount
}

export const calculateFilesDollarAmount = (filesUsage: number, fileMeteredProduct: Product) => {
  const prepaidVolume = getFreeTierUpTo(fileMeteredProduct)!
  const unitAmount = getPaidTierUnitAmount(fileMeteredProduct)!
  return Math.max(0, filesUsage - prepaidVolume) * unitAmount
}

// Find the closest 10^3 magnitude, and return along with the
// term for that magnitude (million, billion, or trillion)
export const getNearestMagnitudeAndTerm = (num: number): [n: number, s: string] => {
  const absoluteMagnitude = Math.floor(Math.log10(num))
  if (absoluteMagnitude >= 12) {
    return [12, 'trillion']
  }
  if (absoluteMagnitude >= 9) {
    return [9, 'billion']
  }
  if (absoluteMagnitude >= 6) {
    return [6, 'million']
  }
  // No need to abbreviate thousands
  return [0, '']
}

/**
 * Format usage and free limit so they can be expressed as a
 * human-readable fraction, where the numerator and denominator
 * have the same magnitude
 * e.g. [54_145_456, 10_010_000_000] -> [".54 billion", "10 billion"]
 *  */
export const formatUsageNumbers = ([usage, freeLimit]: [usage: number, freeLimit: number]) => {
  const [magnitude, term] = getNearestMagnitudeAndTerm(freeLimit)
  const usageTruncated = (usage / 10 ** magnitude).toFixed(2)
  const usageFormatted = [usageTruncated, term].join(' ')
  const freeLimitTruncated = (freeLimit / 10 ** magnitude).toFixed()
  const freeLimitFormatted = [freeLimitTruncated, term].join(' ')
  return [usageFormatted, freeLimitFormatted]
}
